// Referral helpers — Phase 1
import { supabase } from "@/integrations/supabase/client";
import { isExternalAvatarUrl } from "@/lib/avatar";

const REF_KEY = "wekit_referral_code";
const REF_HANDLE_KEY = "wekit_referral_handle";
const REF_DISMISSED_KEY = "wekit_referral_banner_dismissed";

export function captureReferralFromUrl(handle: string) {
  if (!handle) return;
  const clean = handle.replace(/[^a-zA-Z0-9-]/g, "").toLowerCase();
  if (!clean) return;
  try {
    localStorage.setItem(REF_HANDLE_KEY, clean);
    localStorage.setItem(REF_KEY, `REF-${clean.toUpperCase()}`);
  } catch {
    /* ignore */
  }
}

export function getStoredReferralCode(): string | null {
  try {
    return localStorage.getItem(REF_KEY);
  } catch {
    return null;
  }
}

export function getStoredReferralHandle(): string | null {
  try {
    return localStorage.getItem(REF_HANDLE_KEY);
  } catch {
    return null;
  }
}

export function clearStoredReferral() {
  try {
    localStorage.removeItem(REF_KEY);
    localStorage.removeItem(REF_HANDLE_KEY);
    localStorage.removeItem(REF_DISMISSED_KEY);
  } catch {
    /* ignore */
  }
}

export function isReferralBannerDismissed(): boolean {
  try {
    return localStorage.getItem(REF_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissReferralBanner() {
  try {
    localStorage.setItem(REF_DISMISSED_KEY, "1");
  } catch {
    /* ignore */
  }
}

export type ReferrerCard = {
  handle: string;
  firstName: string;
  avatarUrl: string | null;
};

export async function fetchReferrerCard(
  handle: string,
): Promise<ReferrerCard | null> {
  const { data, error } = await supabase.rpc("get_referrer_card", {
    _handle: handle,
  });
  if (error || !data || data.length === 0) return null;
  const row = data[0] as { handle: string; first_name: string; avatar_url: string | null };
  return {
    handle: row.handle,
    firstName: row.first_name,
    // Referral cards are rendered to signed-out visitors, who cannot sign a
    // private storage path — only pass through absolute URLs.
    avatarUrl: isExternalAvatarUrl(row.avatar_url ?? "") ? row.avatar_url : null,
  };
}

export type ReferralAssets = {
  handle: string;
  code: string;
  url: string;
};

export function buildReferralUrl(handle: string) {
  // Marketing-friendly canonical URL
  return `https://wekit.ai/ref/${handle}`;
}

export async function fetchMyReferralAssets(
  userId: string,
): Promise<ReferralAssets | null> {
  const { data: profile } = await supabase
    .from("profiles")
    .select("referral_handle")
    .eq("id", userId)
    .maybeSingle();

  if (!profile?.referral_handle) return null;
  const handle = profile.referral_handle;
  return {
    handle,
    code: `REF-${handle.toUpperCase()}`,
    url: buildReferralUrl(handle),
  };
}

export async function fetchMyReferralStats(userId: string) {
  const { data } = await supabase
    .from("referrals")
    .select("id, status, created_at, converted_at")
    .eq("referrer_user_id", userId);

  const all = data ?? [];
  return {
    total: all.length,
    converted: all.filter((r) => r.status === "converted").length,
    pending: all.filter((r) => r.status === "pending").length,
  };
}

export type RecentInvite = {
  id: string;
  status: "pending" | "converted" | string;
  created_at: string;
  converted_at: string | null;
};

export async function fetchRecentInvites(
  userId: string,
  limit = 5,
): Promise<RecentInvite[]> {
  const { data } = await supabase
    .from("referrals")
    .select("id, status, created_at, converted_at")
    .eq("referrer_user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);
  return (data ?? []) as RecentInvite[];
}

export type SocialProof = {
  friendsJoined: number;
  joinedToday: number;
  totalWekiteers: number;
};

export async function fetchSocialProof(): Promise<SocialProof> {
  const { data, error } = await supabase.rpc("get_referral_social_proof");
  if (error || !data || data.length === 0) {
    return { friendsJoined: 0, joinedToday: 0, totalWekiteers: 0 };
  }
  const row = data[0] as {
    friends_joined: number;
    joined_today: number;
    total_wekiteers: number;
  };
  return {
    friendsJoined: Number(row.friends_joined ?? 0),
    joinedToday: Number(row.joined_today ?? 0),
    totalWekiteers: Number(row.total_wekiteers ?? 0),
  };
}

// --- Funnel analytics ----------------------------------------------------
export type FunnelDay = {
  day: string; // YYYY-MM-DD
  clicks: number;
  signups: number;
  conversions: number;
};

export async function logReferralClick(handle: string, source = "direct") {
  if (!handle) return;
  try {
    await supabase.rpc("log_referral_click", {
      _handle: handle,
      _source: source,
      _ua: typeof navigator !== "undefined" ? navigator.userAgent : null,
      _referer: typeof document !== "undefined" ? document.referrer : null,
    });
  } catch {
    /* best-effort */
  }
}

export async function fetchReferralFunnel(days = 30): Promise<FunnelDay[]> {
  const { data, error } = await supabase.rpc("get_referral_funnel", { _days: days });
  if (error || !data) return [];
  return (data as Array<{ day: string; clicks: number; signups: number; conversions: number }>).map(
    (r) => ({
      day: r.day,
      clicks: Number(r.clicks ?? 0),
      signups: Number(r.signups ?? 0),
      conversions: Number(r.conversions ?? 0),
    }),
  );
}

export function funnelToCsv(rows: FunnelDay[]): string {
  const header = "date,clicks,signups,conversions,click_to_signup_pct,signup_to_conversion_pct";
  const body = rows
    .map((r) => {
      const cs = r.clicks > 0 ? ((r.signups / r.clicks) * 100).toFixed(1) : "";
      const sc = r.signups > 0 ? ((r.conversions / r.signups) * 100).toFixed(1) : "";
      return `${r.day},${r.clicks},${r.signups},${r.conversions},${cs},${sc}`;
    })
    .join("\n");
  return `${header}\n${body}\n`;
}

// --- Source breakdown -----------------------------------------------------
export type SourceRow = {
  source: string;
  clicks: number;
  signups: number;
  conversions: number;
};

export async function fetchReferralSources(days = 30): Promise<SourceRow[]> {
  const { data, error } = await supabase.rpc("get_referral_sources", { _days: days });
  if (error || !data) return [];
  return (data as Array<{ source: string; clicks: number; signups: number; conversions: number }>).map(
    (r) => ({
      source: r.source || "direct",
      clicks: Number(r.clicks ?? 0),
      signups: Number(r.signups ?? 0),
      conversions: Number(r.conversions ?? 0),
    }),
  );
}

export function sourcesToCsv(rows: SourceRow[]): string {
  const header = "source,clicks,signups,conversions";
  const body = rows
    .map((r) => `${r.source},${r.clicks},${r.signups},${r.conversions}`)
    .join("\n");
  return `${header}\n${body}\n`;
}

// --- Channel-specific share copy ------------------------------------------
export type ShareChannel = "whatsapp" | "linkedin" | "twitter" | "email";

export function shareMessageFor(
  channel: ShareChannel,
  firstName: string | null,
  url: string,
): { subject?: string; body: string } {
  const me = firstName?.trim() || "I";
  switch (channel) {
    case "whatsapp":
      return {
        body: `Hello${me === "I" ? "" : ", " + me + " here"}. I recently took the WeKIT™ Career Clarity 360 assessment and found it genuinely helpful in clarifying my direction. You can try it with ₹100 off using my link: ${url}`,
      };
    case "linkedin":
      return {
        body: `Most career advice is generic. WeKIT™ Career Clarity 360 mapped my strengths to 5,000+ careers of the future and showed me a real path forward.\n\nIf you're a student, parent, or early-career professional rethinking your direction — this is worth an hour.\n\n₹100 off through my link: ${url}`,
      };
    case "twitter":
      return {
        body: `Spent an hour on @WeKIT™ Career Clarity 360. Got a personalised map of careers that fit who I actually am. Wild.\n\n₹100 off: ${url}`,
      };
    case "email":
      return {
        subject: "A 30-minute assessment that changed how I see my career",
        body: `Hi,\n\nI recently took the WeKIT™ Career Clarity 360 assessment and wanted to share it with you. It's an AI-powered tool that maps your strengths, interests, and values to real career pathways — including options most career counsellors won't mention.\n\nI'm sharing my personal link below — it gives you ₹100 off:\n${url}\n\nWorth a look,\n${me === "I" ? "" : me}`,
      };
  }
}

export function withSource(url: string, source: string): string {
  try {
    const u = new URL(url);
    u.searchParams.set("utm_source", source);
    return u.toString();
  } catch {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}utm_source=${encodeURIComponent(source)}`;
  }
}

export function shareUrlFor(
  channel: ShareChannel,
  firstName: string | null,
  url: string,
): string {
  const tagged = withSource(url, channel);
  const msg = shareMessageFor(channel, firstName, tagged);
  const text = encodeURIComponent(msg.body);
  switch (channel) {
    case "whatsapp":
      return `https://wa.me/?text=${text}`;
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(tagged)}`;
    case "twitter":
      return `https://twitter.com/intent/tweet?text=${text}`;
    case "email":
      return `mailto:?subject=${encodeURIComponent(msg.subject ?? "")}&body=${text}`;
  }
}

// --- Reward tiers ---------------------------------------------------------
export type RewardKey =
  | "future_skills_report"
  | "ai_career_companion"
  | "career_roadmap"
  | "portfolio_builder"
  | "premium_membership"
  | "affiliate_unlocked";

export type RewardTier = {
  count: number;
  unlock: string;
  badge: string;
  emoji: string;
  key: RewardKey;
  cta?: { label: string; href: string };
};

export const REWARD_TIERS: RewardTier[] = [
  {
    count: 1,
    unlock: "Future Skills Report",
    badge: "Explorer",
    emoji: "🧭",
    key: "future_skills_report",
    cta: { label: "Open report", href: "/report" },
  },
  {
    count: 3,
    unlock: "AI Career Companion",
    badge: "Connector",
    emoji: "🤝",
    key: "ai_career_companion",
    cta: { label: "Talk to coach", href: "/coach" },
  },
  {
    count: 5,
    unlock: "Career Roadmap",
    badge: "Navigator",
    emoji: "🗺️",
    key: "career_roadmap",
    cta: { label: "Open roadmap", href: "/growth-plan" },
  },
  {
    count: 10,
    unlock: "Portfolio Builder",
    badge: "Mentor",
    emoji: "🌱",
    key: "portfolio_builder",
    cta: { label: "Build resume", href: "/resume" },
  },
  {
    count: 25,
    unlock: "Premium Membership",
    badge: "Ambassador",
    emoji: "⭐",
    key: "premium_membership",
  },
  {
    count: 50,
    unlock: "Affiliate Commissions",
    badge: "Future of Work Champion",
    emoji: "🏆",
    key: "affiliate_unlocked",
    cta: { label: "Affiliate dashboard", href: "/affiliate" },
  },
];

export function nextTier(converted: number): RewardTier | null {
  return REWARD_TIERS.find((t) => converted < t.count) ?? null;
}

export function earnedTiers(converted: number): RewardTier[] {
  return REWARD_TIERS.filter((t) => converted >= t.count);
}
